import { Tabs } from '@opallabs/satellite'
import { NormalizedTask } from '@opallabs/wormhole/types'
import * as sharedStyles from '../Shared/styles'
import { LocalStorageKeys } from '../types'
import { useSelectedSubTab } from '../useSelectedSubTab'
import { CompletedPanel } from './SubPanels/CompletedPanel'
import { TodoPanel } from './SubPanels/TodoPanel'
import { UpcomingPanel } from './SubPanels/UpcomingPanel'
import { TaskPanelProps } from './types'
import { useCheckpointCreators } from './useCheckpointCreators'

export const TaskPanel: React.FC<TaskPanelProps> = ({
  completedTasksLoading,
  createCheckpoint,
  getCompletedTasks,
  getOverdueTasks,
  getTodoTasks,
  getUpcomingTasks,
  onTaskCheckboxClick,
  onTaskStarClick,
  overdueTasksLoading,
  setTaskPreviewCard,
  status,
  tasks,
  theme = 'light',
  todoTasksLoading,
  upcomingTasksLoading,
}) => {
  const {
    createCompletedCheckpoint,
    createOverdueCheckpoint,
    createToDoCheckpoint,
    createUpcomingCheckpoint,
  } = useCheckpointCreators({
    completedTasksLoading,
    createCheckpoint,
    overdueTasksLoading,
    tasks,
    todoTasksLoading,
    upcomingTasksLoading,
  })

  const { onTabChange, selectedIndex } = useSelectedSubTab({
    localStorageKey: LocalStorageKeys.tasksSubTab,
    orderedTabs: ['to-do', 'upcoming', 'completed'],
  })

  const tabHasUnseen = (
    subTab: 'todo' | 'completed' | 'upcoming' | 'overdue'
  ): boolean => status !== null && status.tasks[subTab].unseen > 0

  return (
    <Tabs
      onSelect={onTabChange}
      selectedIndex={selectedIndex}
      styleOverrides={{ tabsWrapper: sharedStyles.subTabWrapper }}
    >
      <Tabs.List
        styleOverrides={{ tabList: sharedStyles.subTabList({ theme }) }}
      >
        <Tabs.Tab
          key="tab-task-todo"
          styleOverrides={{
            baseTab: sharedStyles.subTab({
              hasUnseen: tabHasUnseen('todo') || tabHasUnseen('overdue'),
              theme,
            }),
            selectedTab: sharedStyles.subTabSelected({ theme }),
          }}
        >
          To Do
        </Tabs.Tab>
        <Tabs.Tab
          key="tab-task-upcoming"
          styleOverrides={{
            baseTab: sharedStyles.subTab({
              hasUnseen: tabHasUnseen('upcoming'),
              theme,
            }),
            selectedTab: sharedStyles.subTabSelected({ theme }),
          }}
        >
          Upcoming
        </Tabs.Tab>
        <Tabs.Tab
          key="tab-task-completed"
          styleOverrides={{
            baseTab: sharedStyles.subTab({
              hasUnseen: tabHasUnseen('completed'),
              theme,
            }),
            selectedTab: sharedStyles.subTabSelected({ theme }),
          }}
        >
          Completed
        </Tabs.Tab>
      </Tabs.List>
      <Tabs.Panel styleOverrides={{ tabPanel: sharedStyles.subTabPanel }}>
        <TodoPanel
          createOverdueCheckpoint={createOverdueCheckpoint}
          createToDoCheckpoint={createToDoCheckpoint}
          getOverdueTasks={getOverdueTasks}
          getTodoTasks={getTodoTasks}
          isLoading={todoTasksLoading || overdueTasksLoading}
          onOverdueCheckboxClick={(task: NormalizedTask): void =>
            onTaskCheckboxClick(task)
          }
          onOverdueStarClick={(task: NormalizedTask): void =>
            onTaskStarClick(task)
          }
          onTodoCheckboxClick={(task: NormalizedTask): void =>
            onTaskCheckboxClick(task)
          }
          onTodoStarClick={(task: NormalizedTask): void =>
            onTaskStarClick(task)
          }
          overdueTasks={tasks.overdue}
          setTaskPreviewCard={(task: NormalizedTask | null): void =>
            setTaskPreviewCard(task)
          }
          todoTasks={tasks.todo}
        />
      </Tabs.Panel>
      <Tabs.Panel styleOverrides={{ tabPanel: sharedStyles.subTabPanel }}>
        <UpcomingPanel
          isLoading={upcomingTasksLoading}
          createCheckpoint={createUpcomingCheckpoint}
          tasks={tasks.upcoming}
          getTasks={getUpcomingTasks}
          setTaskPreviewCard={(task: NormalizedTask | null): void =>
            setTaskPreviewCard(task)
          }
          onTaskStarClick={(task: NormalizedTask): void =>
            onTaskStarClick(task)
          }
          onTaskCheckboxClick={(task: NormalizedTask): void =>
            onTaskCheckboxClick(task)
          }
        />
      </Tabs.Panel>
      <Tabs.Panel styleOverrides={{ tabPanel: sharedStyles.subTabPanel }}>
        <CompletedPanel
          isLoading={completedTasksLoading}
          createCheckpoint={createCompletedCheckpoint}
          tasks={tasks.completed}
          getTasks={getCompletedTasks}
          setTaskPreviewCard={(task: NormalizedTask | null): void =>
            setTaskPreviewCard(task)
          }
          onTaskStarClick={(task: NormalizedTask): void =>
            onTaskStarClick(task)
          }
          onTaskCheckboxClick={(task: NormalizedTask): void =>
            onTaskCheckboxClick(task)
          }
        />
      </Tabs.Panel>
    </Tabs>
  )
}
